<template>
  <!------------------------------------------
    This component displays the contressional district to service areas overlap.
  -------------------------------------------->
  <div id="disaster-estimates">
    <PageTitle title="Disaster Estimates"/>
    <SideMarginsWrapper maxWidth="1200">
      <AboutResource
        about='Estimates of the number of people affected by natural disasters are used by GRPA for funding requests and by OPP for allocating disaster grants. This resource provides estimates of the LSC eligible population and total population for counties designated in federal disaster declarations, including a breakdown by grantee (Basic Field General service area) and county. Alternatively, select the "Counties by State" tab to get LSC eligible and total population estimates for all counties in a state or for a custom selection of counties.'
        fyi='This tool uses the number of people with household income below 125% of the federal poverty threshold as an estimate of the LSC eligible population. Actual LSC eligibility is based on HHS Poverty Guidelines and income level exemptions.'
        source="Information about disaster declarations are obtained in real time and reflect the most recent data available from the FEMA API. All population data -- regardless of the disaster year -- are from the Census Bureau's 2016-2020 American Community Survey 5-year estimates. Please contact ODGA if you need population estimates from prior years."
        :techContact1="{ name: 'Jordan', email: 'laytonj@lsc.gov', department: 'ODGA' }"
      >
        <template v-slot:beneath>
          <div style="color:#b31308; font-style:italic">Heads up: This tool pulls data live from FEMA and the Census Bureau. If data seem to load slowly, please reload or wait a minute and try again.</div>
          <AlertContentUpdate>
            <div>
              Annual reports of all federally declared disasters (declared during the year, regardless of when the disaster occurred) are available <a href="https://lsc-live.box.com/s/ava4cmm1uapia4rr163vbeiow8c6oxvk">here in Box</a>, with a new report added each January.
            </div>
          </AlertContentUpdate>
        </template>
      </AboutResource>
      <div id="estimate-tabs">
        <v-tabs
          v-model="tab"
          color="#165991"
          centered
        >
          <v-tabs-slider color="#165991"></v-tabs-slider>
          <v-tab
            v-for="tab in tabs"
            :key="tab"
          >
            {{ tab }}
          </v-tab>
        </v-tabs>
      </div>
      <br>
      <div v-if="tab === 0">
        <h2>Population Estimates for Federal Disaster Declarations</h2>
        <div class="tab-instructions">This list of disaster declarations is ordered by the declaration date shown, starting with the most recent. This page gets the data from FEMA in real-time, so you are seeing the latest data available from FEMA. Additional disasters can be loaded using the "Load more" button at the bottom of the list (back to 1953). Click on a disaster declaration to see its designated areas as well as population estimates based on the latest Census data.</div>
        <div id="disaster-list-filters">
          <h3>Filter by</h3>
          <div id="filters-row">
            <div>
              <div class="filter-header">State</div>
              <StateSelect @stateSelected="handleStateChange" :simpleVersion="true" :allOption="true" statePassedAsProp="*" :neverUpdateUrlParam="true" />
            </div>
            <div>
              <div class="filter-header">Incident type</div>
              <div>
                <v-select
                  class="filter-select"
                  v-model="incidentTypeFilter"
                  :items="Object.keys(iconsPerIncidentType).sort()"
                  dense
                ></v-select>
              </div>
            </div>
            <div>
              <div class="filter-header">Declaration type</div>
              <div>
                <v-select
                  class="filter-select"
                  v-model="declarationTypeFilter"
                  :items="Object.keys(declarationTypeColors).sort()"
                  dense
                ></v-select>
              </div>
            </div>
            <div>
              <v-btn
                elevation="2"
                @click="getWithFilter"
                class="load-more"
              >Apply filters</v-btn>
            </div>
          </div>
        </div>
        <div id="disasters-expansion-panels" v-if="disastersList.length > 0">
          <v-expansion-panels popout :multiple="true">
            <v-expansion-panel
              v-for="d in disastersList"
              :key="d.id + d.disasterNumber + d.stateCode"
              :id="'disaster-' + d.disasterNumber"
              class="disaster-row"
              @click="prepareDisasterSummary(d.disasterNumber, d.stateCode)"
            >
              <v-expansion-panel-header class="disaster-header">
                <div class="header-flex">
                  <div class="header-left">
                    <v-icon size="32" class="incident-icon" color="#bababa">
                      {{ iconsPerIncidentType[d.incidentType] }}
                    </v-icon>
                    <div class="header-flex-column">
                      <div>{{ d.declarationDate.substring(5,7) }}/{{ d.declarationDate.substring(8,10) }}/{{ d.declarationDate.substring(0,4) }}<span class="header-name">{{ d.disasterName }} - {{ d.stateName }}</span></div>
                      <div class="disaster-number">#{{ d.disasterNumber }}</div>
                    </div>
                  </div>
                  <div class="header-right">
                    <v-chip class="declaration-type" text-color="black" outlined label :color="declarationTypeColors[d.declarationType]">{{ d.declarationType }}</v-chip>
                  </div>
                </div>
              </v-expansion-panel-header>

              <v-expansion-panel-content class="disaster-content">
                <div class="link-to-map" v-if="loading[d.disasterNumber] === false">
                  <div class="assistance-type-radio" v-if="d.disasterNumber in disasterCounties && disasterCounties[d.disasterNumber]['any'].length > 0">
                    <v-radio-group
                      v-model="assistanceRadio[d.disasterNumber]"
                      hide-details
                      row
                    >
                      <v-radio
                        label="Any FEMA assistance"
                        value="any"
                      ></v-radio>
                      <v-radio
                        label="Individual Assistance only"
                        value="ia"
                      ></v-radio>
                    </v-radio-group>
                  </div>
                  <div>
                    <v-icon color="primary" class="link-to-map-icon" size="25">{{ linkIcon }}</v-icon>See the <a :href="`https://www.fema.gov/disaster/${d.disasterNumber}/designated-areas`" target="_blank">FEMA designated areas & map</a>
                  </div>
                </div>

                <div class="disaster-content-loading" v-if="loading[d.disasterNumber] === true">
                  <div class="spinner"></div>
                  <div>Loading...</div>
                </div>

                <div class="disaster-content-error" v-if="errors[d.disasterNumber]">{{ errors[d.disasterNumber]}} </div>

                <div class="disaster-content-margins" v-if="loading[d.disasterNumber] === false">

                  <div class="grantee-county-summary" v-if="d.disasterNumber in disasterCounties && disasterCounties[d.disasterNumber][assistanceRadio[d.disasterNumber]].length > 0 && ['VI','AS','GU','MP'].indexOf(d.stateCode) === -1">
                    <div class="eligible-total"><span class="eligible-total-num">{{ sumOfArray(disasterCounties[d.disasterNumber][assistanceRadio[d.disasterNumber]].map(x => x.eligible )) }}</span> LSC eligible people in {{ disasterCounties[d.disasterNumber][assistanceRadio[d.disasterNumber]].length }} designated counties or county equivalents. <span class="pop-total">(Total population: <span class="pop-total-num">{{ sumOfArray(disasterCounties[d.disasterNumber][assistanceRadio[d.disasterNumber]].map(x => x.pop )) }}</span>).</span></div>
                    <div class="grantee-summary">
                      <div class="summary-subheader">By grantee/service area</div>
                      <table>
                        <thead>
                          <td>Grantee (Basic Field General service area)</td>
                          <td>LSC eligible in associated designated counties</td>
                          <td>Total population in associated designated counties</td>
                        </thead>
                        <tbody>
                          <tr v-for="area in relevantAreas[d.disasterNumber]" :key="area.serv_area + area.org_name" :class="disasterCounties[d.disasterNumber][assistanceRadio[d.disasterNumber]].filter(x => x.serv_areas.includes(area.serv_area)).length > 0 ? '' : 'hidden-row'">
                            <td>{{ area.org_name }} ({{ area.serv_area }})</td>
                            <td>{{ sumByArea(area.serv_area, disasterCounties[d.disasterNumber][assistanceRadio[d.disasterNumber]], 'eligible') }}</td>
                            <td>{{ sumByArea(area.serv_area, disasterCounties[d.disasterNumber][assistanceRadio[d.disasterNumber]], 'pop') }}</td>
                          </tr>
                        </tbody>
                      </table>
                      <div class="table-note" v-if="dividedCounties.length > 0"> {{ dividedCounties.join(', ') }} <span v-if="dividedCounties.length === 1">is</span><span v-if="dividedCounties.length > 1">are</span> served by two grantees. In the above estimates, the population for <span v-if="dividedCounties.length === 1">this county has</span><span v-if="dividedCounties.length > 1">these counties have</span> been counted to both grantees. If you need a more precise breakdown for <span v-if="dividedCounties.length === 1">this county</span><span v-if="dividedCounties.length > 1">these counties</span>, please reach out to ODGA.</div>
                    </div>
                    <div class="county-summary">
                      <div class="summary-subheader">By designated county</div>
                      <div class="counties-table" :class="disasterCounties[d.disasterNumber][assistanceRadio[d.disasterNumber]].length > 9 ? 'table-overflow' : ''">
                        <table>
                          <thead>
                            <td>County</td>
                            <td>LSC eligible</td>
                            <td>Total population</td>
                            <td>Service area(s)</td>
                            <td>
                              <v-tooltip bottom color="#165991">
                                <template v-slot:activator="{ on }">
                                  <div v-on="on">
                                    Assistance types
                                    <v-icon color="grey" right class="link-to-map-icon" size="25">{{ tooltipIcon }}</v-icon>
                                  </div>
                                </template>
                                <span>IA = Individual Assistance,<br> PA = Public Assistance,<br> HM = Hazard Mitigation</span>
                              </v-tooltip>
                            </td>
                          </thead>
                          <tbody>
                            <tr v-for="county in disasterCounties[d.disasterNumber][assistanceRadio[d.disasterNumber]]" :key="county.county + county.pop">
                              <td>{{ county.county }}</td>
                              <td>{{ formatThousandsNoDecimal(county.eligible) }}</td>
                              <td>{{ formatThousandsNoDecimal(county.pop) }}</td>
                              <td>{{ county.serv_areas.join(', ') }}</td>
                              <td>{{ county.assistance_types.join(', ') }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  <!-- NOTHING ELIGIBLE FOR I.A. -->
                  <div v-if="d.disasterNumber in disasterCounties && disasterCounties[d.disasterNumber][assistanceRadio[d.disasterNumber]].length === 0 && ['VI','AS','GU','MP'].indexOf(d.stateCode) === -1" class="no-declared-county">
                    None of the designated areas for this disaster were eligible for Individual Assistance from FEMA. Select "Any FEMA assistance" above to view designated areas granted other types of FEMA assistance.
                  </div>

                  <!-- TERRITORIES BESIDES PUERTO RICO -->
                  <div v-if="!(d.disasterNumber in disasterCounties)" class="no-declared-county">
                    <span v-if="['VI','AS','GU','MP'].indexOf(d.stateCode) > -1">U.S. territories besides Puerto Rico will not have LSC eligible population estimates available here nor on the Census Bureau's OnTheMap tool. Please reach out to ODGA to estimate the LSC eligible population for this disaster.</span><span v-else>This disaster had no designated counties or county equivalents, so no estimates of the LSC eligible population are provided here. You can use the OnTheMap tool linked below, or, if you want population estimates for counties associated with these designated areas and have identified those counties, you can use the Counties by State tab at the top of this page.</span> 
                  </div>

                  <!-- IF *ONLY* NON-COUNTIES WERE DESIGNATED, LINK TO ONTHEMAP TOOL -->
                  <div class="other-areas-estimate" v-if="!(d.disasterNumber in disasterCounties) && d.disasterNumber in disasterNonCounties && disasterNonCounties[d.disasterNumber][assistanceRadio[d.disasterNumber]].length > 0 && ['VI','AS','GU','MP'].indexOf(d.stateCode) === -1">
                    <v-dialog
                      transition="dialog-top-transition"
                      max-width="1000"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          v-bind="attrs"
                          v-on="on"
                          text
                          class="onthemap-info-button"
                        >
                          <v-icon size="27" left>
                            {{ onthemap.icon }}
                          </v-icon>
                          Estimate eligibility using Census' OnTheMap tool
                        </v-btn>
                      </template>
                      <template v-slot:default="dialog">
                        <v-card id="onthemap-dialog-contents">
                          <v-card-text>
                            <div id="onthemap-intro">
                              The Census Bureau's <a target="_blank" href="https://onthemap.ces.census.gov/em/">OnTheMap for Emergency Management</a> tool provides maps and demographic/economic data for disaster events. Go to the site and search for the disaster's number ({{ d.disasterNumber }}) or name to find its page, which will look similar to the screenshot below. To get an estimate of the LSC eligible population in the affected area, open the data category "Ratio of Income to Poverty Level in the Past 12 Months" and then sum the first three numbers: Under .50, .50 to .99, and 1.00 to 1.24. This is the number of people in the area below 125% of the federal poverty threshold.
                            </div>
                            <img id="onthemap-img" :src="require(`@/assets/images/other/onthemap_instruction.png`)">
                          </v-card-text>
                          <v-card-actions class="justify-end">
                            <v-btn
                              text
                              @click="dialog.value = false"
                            >Close</v-btn>
                          </v-card-actions>
                        </v-card>
                      </template>
                    </v-dialog>
                  </div>

                  <!-- LIST NON-COUNTY DESIGNATED AREAS -->
                  <div class="other-areas" v-if="d.disasterNumber in disasterNonCounties && disasterNonCounties[d.disasterNumber][assistanceRadio[d.disasterNumber]].length > 0">
                    <div class="summary-subheader">Non-county designated areas</div>
                    <table>
                      <tbody>
                        <tr v-for="area in disasterNonCounties[d.disasterNumber][assistanceRadio[d.disasterNumber]]" :key="area.designatedArea">
                          <td>{{ area.designatedArea }}</td>
                        </tr>
                      </tbody>
                    </table>
                    <div class="table-note" v-if="d.disasterNumber in disasterCounties && disasterCounties[d.disasterNumber][assistanceRadio[d.disasterNumber]].length > 0">Note that non-county areas -- despite being listed separately in the disaster declaration -- are often part of counties (and the associated population estimates) listed above.</div>
                  </div>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
          <v-btn v-if="noMoreToLoad === false"
            elevation="2"
            @click="getMoreDisasters"
            class="load-more"
          >Load more</v-btn>
        </div>
        <div v-if="noMoreToLoad === true" id="no-more-to-load">No more records found</div>
      </div>

      <div v-if="tab === 1">
        <h2>Population Estimates for Counties by State</h2>
        <div class="tab-instructions">Use this tool to get total and LSC eligible population estimates for selected counties, <span class="emphasis">regardless of inclusion in a federal disaster declaration</span>. This is most useful when the disaster declaration does not include counties in its designated areas (i.e. was declared only for a tribal area) and you want to look up a selection of counties instead, or if you want to retrieve population estimates for counties that were not included in a disaster's designated areas. Start by selecting a state. For territories apart from Puerto Rico, please reach out to ODGA for estimates.</div><br>
        <div class="select-state-section">
          <h3>Select a state</h3>
          <div class="state-select state-county-select">
            <StateSelect 
              @stateSelected="handleStateChange" 
              :simpleVersion="true" 
              :includePR="true" 
              :includeAS="false" 
              :includeMP="false" 
              :includeVI="false" 
              :includeGU="false" 
            />
          </div>
        </div>
        <div class="select-county-section" v-if="countyDropdownItems.length > 0">
          <div class="select-county-header-row">
            <h3>Select counties</h3>
            <div class="counties-count">({{ countyDropdownSelected.length }} selected of {{ countyDropdownItems.length }})</div>
            <div class="select-county-instruction">You can type to search below.</div>
          </div>
          <div class="select-all-counties-container">
            <v-checkbox
              :hide-details="true"
              v-model="selectAllCounties"
              label="Select all counties"
            ></v-checkbox>
            <div> </div>
          </div>
          <div class="county-select state-county-select" :class="countyDropdownSelected.length > 20 ? 'table-overflow' : ''">
            <v-autocomplete
              v-model="countyDropdownSelected"
              :items="countyDropdownItems"
              item-text="name"
              item-value="fips"
              clearable
              auto-select-first
              small-chips
              deletable-chips
              multiple
            ></v-autocomplete>
          </div>
        </div>
        <v-btn
          elevation="2"
          @click="prepareStateSummary"
          class="ready-btn"
          :disabled="countyDropdownSelected.length > 0 ? false : true"
        >Load the Data</v-btn>
        <div ref="stateResults" id="state-results" class="disaster-content state-content-margins" v-if="stateLoadedOnce === true">
          <div class="state-content-header">Results</div>
          <div class="disaster-content-loading" v-if="stateLoading === true">
            <div class="spinner"></div>
            <div>Loading...</div>
          </div>
          <div class="grantee-county-summary" v-if="stateLoading === false">
            <div class="eligible-total"><span class="eligible-total-num">{{ sumOfArray(stateRequestedCounties.map(x => x.eligible )) }}</span> LSC eligible people in {{ stateRequestedCounties.length }} counties or county equivalents.  <span class="pop-total">(Total population: <span class="pop-total-num">{{ sumOfArray(stateRequestedCounties.map(x => x.pop )) }}</span>).</span></div>
            <div class="grantee-summary">
              <div class="summary-subheader">By grantee/service area</div>
              <table>
                <thead>
                  <td>Grantee (Basic Field General service area)</td>
                  <td>LSC eligible in selected counties</td>
                  <td>Total population in selected counties</td>
                </thead>
                <tbody>
                  <tr v-for="area in stateRelevantAreas" :key="area.serv_area + area.org_name">
                    <td>{{ area.org_name }} ({{ area.serv_area }})</td>
                    <td>{{ sumByArea(area.serv_area, stateRequestedCounties, 'eligible') }}</td>
                    <td>{{ sumByArea(area.serv_area, stateRequestedCounties, 'pop') }}</td>
                  </tr>
                </tbody>
              </table>
              <div class="table-note" v-if="dividedCounties.length > 0"> {{ dividedCounties.join(', ') }} <span v-if="dividedCounties.length === 1">is</span><span v-if="dividedCounties.length > 1">are</span> served by two grantees. In the above estimates, the population for <span v-if="dividedCounties.length === 1">this county has</span><span v-if="dividedCounties.length > 1">these counties have</span> been counted to both grantees. If you need a more precise breakdown for <span v-if="dividedCounties.length === 1">this county</span><span v-if="dividedCounties.length > 1">these counties</span>, please reach out to ODGA.</div>
            </div>
            <div class="county-summary">
              <div class="summary-subheader">By county</div>
              <div class="counties-table" :class="stateRequestedCounties.length > 4 ? 'table-overflow' : ''">
                <table>
                  <thead>
                    <td>County</td>
                    <td>LSC eligible</td>
                    <td>Total population</td>
                    <td>Service area(s)</td>
                  </thead>
                  <tbody>
                    <tr v-for="county in stateRequestedCounties" :key="county.county + county.pop">
                      <td>{{ county.county }}</td>
                      <td>{{ formatThousandsNoDecimal(county.eligible) }}</td>
                      <td>{{ formatThousandsNoDecimal(county.pop) }}</td>
                      <td>{{ county.serv_areas.join(', ') }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SideMarginsWrapper>
  </div>
</template>

<script>
const d3_array = require('d3-array')
import { format } from 'd3-format'
import PageTitle from '@/components/common/PageTitle'
import StateSelect from '@/components/select/StateSelect'
import AboutResource from '@/components/common/AboutResource'
import SideMarginsWrapper from '@/components/layout/SideMarginsWrapper'
import AlertContentUpdate from '@/components/common/AlertContentUpdate'
import { mdiInformationOutline, mdiImageFilterHdr, mdiWaves, mdiWeatherSunny, mdiMapMarker, mdiWeatherHurricane, mdiExclamationThick, mdiSlopeDownhill, mdiWeatherSnowyHeavy, mdiVirus, mdiHumanMale, mdiFire, mdiWeatherTornado, mdiWeatherLightning, mdiHomeFlood, mdiHelpCircleOutline } from '@mdi/js';
import { servAreas2024 } from '@/js/serv_areas.js'
import states from '@/js/stFips.js'

export default {
  name: 'DisasterEstimates',
  components: { StateSelect, AboutResource, PageTitle, SideMarginsWrapper, AlertContentUpdate },

  data () {
    return {
      censusKey: '0e3e5795cd0335f3faefb832e3f7c9b086d25998', // MOVE TO ENV
      selectedState: '*',
      tab: 0,
      tabs: [ 'Federal Disaster Declarations', 'Counties by State' ],
      assistanceRadio: {},
      defaultRadioVal: 'any', // 'any' or 'ia'
      disastersList: [],
      disasterCounties: {},
      disasterNonCounties: {},
      relevantAreas: {},
      loading: {},
      dividedCounties: [],
      stateLoading: false,
      stateLoadedOnce: false,
      stateRelevantAreas: [],
      stateRequestedCounties: [],
      countyDropdownItems: [],
      countyDropdownSelected: [],
      selectAllCounties: false,
      noMoreToLoad: false,
      incidentTypeFilter: "All incident types",
      declarationTypeFilter: "All declaration types",
      errors: {},
      pageSize: 25,
      skip: 0,
      linkIcon: mdiMapMarker,
      tooltipIcon: mdiHelpCircleOutline,
      onthemap: {
        onthemapImg: require('@/assets/images/other/onthemap_instruction.png'),
        icon: mdiInformationOutline
      },
      iconsPerIncidentType: {
        "All incident types": null, // Just for populating the filters select menu
        "Hurricane": mdiWeatherHurricane,
        "Typhoon": mdiWeatherHurricane,
        "Severe Storm": mdiWeatherLightning,
        "Tropical Storm": mdiWeatherLightning,
        "Fire": mdiFire,
        "Biological": mdiVirus,
        "Flood": mdiHomeFlood,
        "Snow": mdiWeatherSnowyHeavy,
        "Mud/Landslide": mdiSlopeDownhill,
        "Other": mdiExclamationThick,
        "Drought": mdiWeatherSunny,
        "Tornado": mdiWeatherTornado,
        "Severe Ice Storm": mdiWeatherSnowyHeavy,
        "Winter Storm": mdiWeatherSnowyHeavy,
        "Snowstorm": mdiWeatherSnowyHeavy,
        "Earthquake": mdiExclamationThick,
        "Human Cause": mdiHumanMale,
        "Dam/Levee Break": mdiHomeFlood,
        "Coastal Storm": mdiWeatherHurricane,
        "Volcano": mdiImageFilterHdr,
        "Tsunami": mdiWaves
      },
      declarationTypeColors: {
        'All declaration types': null, // Just for populating the filters select menu
        'Major Disaster': 'red',
        'Emergency': 'orange',
        'Fire Management': 'yellow',
        'Fire Suppression': 'yellow'
      }
    }
  },

  computed: {
    selectedStateAbbrev () { 
      if (this.selectedState === '*') {
        return ''
      } else if (this.selectedState !== '') {
        return states.filter(x => x.fips === String(this.selectedState))[0].st 
      } else {
        return ''
      }
    }
  },

  created () {
    if (this.tab === 0 && this.disastersList.length === 0) {
      this.getMoreDisasters()
    }
    if (this.tab === 1 && this.selectedState !== '') {
      this.getListOfCounties(this.selectedState)
    }
  },

  methods: {
    async getDisastersList () {
      let component = this
      let filters = []
      if (this.selectedStateAbbrev !== '') {
        filters.push(`stateCode eq '${this.selectedStateAbbrev}'`)
      }
      if (this.incidentTypeFilter !== "All incident types") {
        filters.push(`incidentType eq '${this.incidentTypeFilter.replace('/', '%252F')}'`)
      }
      if (this.declarationTypeFilter !== "All declaration types") {
        filters.push(`declarationType eq '${this.declarationTypeFilter}'`)
      }
      let filtersString = filters.length === 0 ? '' : `&$filter=(${filters.join(' and ')})`
      let data = await fetch(`https://www.fema.gov/api/open/v1/FemaWebDisasterDeclarations?$top=${this.pageSize}${filtersString}&$skip=${this.skip}&$orderby=declarationDate desc`)
      .then(function (response) { return response.json() })
      .then(function (json) {
        component.skip = component.skip + component.pageSize
        // Occasionally the first of new "load more" page returns a duplicate entry (same as last of previous) -- TBD why -- so don't include if the disaster # is already present
        let oldDisasters = component.disastersList.map(x => x.disasterNumber)
        let newDisasters = json['FemaWebDisasterDeclarations'].map(x => x.disasterNumber)
        let overlap = newDisasters.filter(x => oldDisasters.includes(x))
        return json['FemaWebDisasterDeclarations'].filter(x => !overlap.includes(x.disasterNumber))
      })
      .catch((err) => {
        console.log(err)
      })
      return data
    },

    async getMoreDisasters () {
      let d = await this.getDisastersList()
      if (d.length === 0) {
        this.noMoreToLoad = true
      } else if (this.disastersList.length > 0) {
        this.disastersList = [...this.disastersList, ...d]
      } else {
        this.disastersList = d
      }
    },

    async getWithFilter () {
      this.skip = 0
      this.disastersList = []
      this.noMoreToLoad = false
      let d = await this.getDisastersList()
      if (d.length === 0) {
        this.noMoreToLoad = true
      } else {
        this.disastersList = d
      }
    },

    async prepareDisasterSummary(disasterNumber, state_abbr) {
      /**
       * Main function for the disasters tab, which works through the steps of retrieving and displaying the details about a disaster declaration, basically everything that occurs while the loading spinner is visible. It is triggered when an accordion menu (disaster) is selected. This includes getting the disaster details from FEMA, separating county from non-county declarations, determining service areas associated with counties, and getting population estimates for counties.
       * 
       * @param {string}  disasterNumber  String of the four digit disaster number from FEMA
       * @param {string}  state_abbr      String of the two digit state FIPS code
       * 
       * @return N/A   Does not return anything, but it sets as reactive properties the county data and non-county data.
       */

      this.$set(this.loading, disasterNumber, true)

      // Don't re-fetch the data if already have it
      if (disasterNumber in this.disasterCounties || disasterNumber in this.disasterNonCounties) { return }

      // Get data about the disaster number
      let disasterDetails = await this.getDisasterDetails(disasterNumber)

      // Determine county from non-county declarations
      let countyNonCountyDeclarations = await this.separateCountyNonCounty(disasterDetails)
      let disasterCountyDeclarations = countyNonCountyDeclarations[0],
          disasterNonCountyDeclarations = countyNonCountyDeclarations[1]

      // Get associated service areas for the state
      let potentialServiceAreas = this.getPotentialServiceAreas(state_abbr)

      // For multi-area states, get each service area's associated counties
      const countiesPerArea = await this.getCountiesPerArea(potentialServiceAreas)

      // Get population estimates from the Census API for the designated counties
      let st_fips = disasterDetails[0]['fipsStateCode']
      if (disasterCountyDeclarations.length > 0) {

        let counties
        let assistanceTypes = disasterCountyDeclarations.map(x => (
          { 
            countyFips: x.fipsCountyCode,
            hmProgramDeclared: x.hmProgramDeclared,
            iaProgramDeclared: x.iaProgramDeclared,
            ihProgramDeclared: x.ihProgramDeclared,
            paProgramDeclared: x.paProgramDeclared,
          }
        ))

        if (disasterCountyDeclarations[0].designatedArea.toLowerCase() === 'statewide') {
          counties = '*'
        } else { 
          counties = disasterCountyDeclarations.map(x => x.fipsCountyCode)
        }

        let r = await this.prepareCountyEstimates(st_fips, counties, potentialServiceAreas, countiesPerArea, assistanceTypes)

        this.$set(this.relevantAreas, disasterNumber, r[1])

        let sortedCounties = r[0].sort((a, b) => a.county.localeCompare(b.county))

        this.$set(
          this.disasterCounties, 
          disasterNumber, 
          { "any": sortedCounties, "ia": sortedCounties.filter(x => x.ia_ih_assistance === true) }
        )
      }

      if (disasterNonCountyDeclarations.length > 0) {
        this.$set(
          this.disasterNonCounties, 
          disasterNumber, 
          { "any": disasterNonCountyDeclarations, "ia": disasterNonCountyDeclarations.filter(x => x.iaProgramDeclared === true || x.ihProgramDeclared === true) }
        )
      }

      // Default to showing individual assistance cases
      if (disasterNonCountyDeclarations.length > 0 || disasterCountyDeclarations.length > 0) {
        this.$set(this.assistanceRadio, disasterNumber, this.defaultRadioVal)
      }

      this.$set(this.loading, disasterNumber, false)
    },

    async getDisasterDetails(disasterNumber) {
      /**
       * Get details about a given disaster number from the FEMA API v2/DisasterDeclarationsSummaries endpoint.
       * 
       * @param {String}  disasterNumber  Four digit identifier for the FEMA disaster declaration.
       * 
       * @return {Array}  Returns an array of objects, each of which is a declared area for the disaster
       */

      let data = await fetch(`https://www.fema.gov/api/open/v2/DisasterDeclarationsSummaries?$filter=disasterNumber eq ${String(disasterNumber)}&$orderby=designatedArea asc&$inlinecount=allpages&$select=fipsStateCode,fipsCountyCode,designatedArea,ihProgramDeclared,iaProgramDeclared,paProgramDeclared,hmProgramDeclared`)
      .then(function (response) { return response.json() })
      .then(function (json) {
        return json
      })
      .catch((err) => {
        this.$set(this.errors, disasterNumber, "Error getting details about this disaster from the FEMA API. The API may be down. Please reload the page, try again later, or reach out to ODGA for these disaster estimates.")
        console.log(err)
      })
      return data['DisasterDeclarationsSummaries']
    },

    async separateCountyNonCounty(disasterDetails) {
      /**
       * Separate county disaster declarations from non-county declarations.
       * 
       * The county FIPS is present even for a declaration of a town within it. 
       * We want to keep ONLY those for which the county was declared, which in the FEMA API show up in the designatedArea variable, like "Greene (County)". 
       * If the designated area is a county (or equivalent) -- contains one of the relevant substrings -- then keep it.
       * Puts all the rest of the declared area into their own array. These are often smaller areas like native american statistical areas that we will already include in the associated county, or we can otherwise come up with the eligibility numbers ad hoc if the disaster was not even at the county scale.
       * EXCEPT for Virginia for which we will check for independent city names in order to include them (they will not have a "county" or equivalent substring), as well as for the other three cities outside of Virginia: Baltimore MD, Carson City NV, and St. Louis MO.
       * 
       * @param {Object}  disasterDetails   JSON response of details about one disaster number from the FEMA API v2/DisasterDeclarationsSummaries endpoint
       * 
       * @return {Array}  Returns an array of two arrays of objects, the first being the entries in the original disasterDetails object that are counties or county equivalents (for which we expect we'll be able to get population estimates from the Census API), and the the second of everything else.
       */

      // Separate county declarations from non-county declarations
      let disasterCountiesList = []
      let disasterNonCountiesList = []
      const countySubstrings = ["(county)", "(municipio)", "(parish)", "(borough)", "(census area)", "(county-equivalent)"]

      for (let x of disasterDetails) {

        // Don't include non-PR territories -- we won't get the ACS estimates
        if (['60', '66', '69', '78'].indexOf(x.fipsStateCode) > -1) {
          disasterNonCountiesList.push(x)
        }

        // Include if "statewide" is the ONLY entry, as sometimes statewide shows up as an entry even if a tiny part of the state was actually declared
        else if (disasterDetails.length === 1 && x.designatedArea.toLowerCase() === 'statewide') {
          disasterCountiesList.push(x)
        }
    
        // Declared area is county or county equivalent
        else if (countySubstrings.some(v => x.designatedArea.toLowerCase().includes(v))) {
          disasterCountiesList.push(x)
        }

        // Independent cities
        else if (
          (x.fipsStateCode === '32' && x.fipsCountyCode === '510') || // Carson City NV
          (x.fipsStateCode === '24' && x.fipsCountyCode === '510') || // Baltimore City
          (x.fipsStateCode === '29' && x.fipsCountyCode === '510') || // St. Louis MO
          (x.fipsStateCode === '51' && ['510', '520', '530', '540', '550', '570', '580', '590', '595', '600', '610', '620', '630', '640', '650', '660', '670', '678', '680', '683', '685', '690', '700', '710', '720', '730', '735', '740', '750', '760', '770', '775', '790', '800', '810', '820', '830', '840'].indexOf(x.fipsCountyCode) > -1)
        ) {
          disasterCountiesList.push(x)          
        }

        // Otherwise, no
        else if (x.designatedArea.toLowerCase() !== 'statewide') {
          disasterNonCountiesList.push(x)
        }
      }

      return [disasterCountiesList, disasterNonCountiesList]
    },

    getPotentialServiceAreas(state_abbr) {
      /**
       * Filters all service areas to those that are Basic Field General and in the provided state.
       * @param {string}  state_abbr  State abbreviation like "FL".
       * @return {array}  Returns an array of objects of BF General service areas in the state.
       */
      return servAreas2024.filter(function(x) { return x.serv_area.substring(0, 2) === state_abbr && x.serv_area_type === 'Basic Field'}).sort((a, b) => a.org_name.localeCompare(b.org_name))
    },

    async getCountiesPerArea(potentialServiceAreas) {
      let x = Promise.all(potentialServiceAreas.map(async x => {
        const resp = await fetch(`${process.env.VUE_APP_BACKEND_NEW_DATA_API}/public/spatial/crosswalks/counties_per/service_area_bf_general/2024/${x.serv_area}.json`)
        .then(function (response) {
          return response.json()
        })
        .catch((err) => { 
          console.log(err) 
        })
        return resp
      }))
      return x
    },

    async prepareCountyEstimates(state_fips, counties, potentialServiceAreas, countiesPerArea, assistanceTypes = null) {
      /**
       * Gets the ACS data for a list of counties in a provided state, formats it as an object, then uses the list of potential service areas (BF General areas in the state) and county to service areas crosswalk to append information to each county on which service area(s) it belongs to.
       * @param {string}  state_fips            String of two digit state FIPS like '01'
       * @param {array}   counties              Array of county FIPS such as ['001', '002']
       * @param {array}   potentialServiceAreas Array of objects that are the service areas in the state
       * @param {array}   countiesPerArea       Array of arrays where each array contains the county fips ('i.e. 01001') that are in the service area, and where the order of the arrays matches the relevant service areas in the potentialServiceAreas array.
       * @param {object}  assistanceTypes      Array of objects like { county: '001', hmProgramDeclared: false, iaProgramDeclared: true, ihProgramDeclared: true, paProgramDeclared: false }
       */

      let component = this
      component.dividedCounties = []

      // Get from Census API
      let counties2DArray = await this.getCountyAcs(state_fips, counties)

      // Convert from 2D JSON to object, add associated service areas
      let countiesArrayOfObjects = []
      let relevantAreas = []

      counties2DArray.forEach(function(countyArr) {
        // Skip the first array (headers)
        if (countyArr[0] === 'NAME') { return }

        // The first position in the Census data array for the county is county name, second is eligible pop, third is state fips, fourth county fips
        let countySansState = countyArr[0].substr(0, countyArr[0].lastIndexOf(","))
        let countyObject = {county: countySansState, eligible: +countyArr[1], pop: +countyArr[2], state_fips: countyArr[3], county_fips: countyArr[4], serv_areas: []}


        // If provided, add info about which types of assistance were awarded by FEMA
        let assistanceTypesArray = []
        if (assistanceTypes !== null) {
          let obj = assistanceTypes.filter(x => x.countyFips === countyArr[4])[0]

          // Using index-1 because the first index of these arrays of assistance types should line up with the second in the countyObjects as the first county object index gets skipped (headers)
          if (obj.ihProgramDeclared === true || obj.iaProgramDeclared === true) {
            countyObject['ia_ih_assistance'] = true
            assistanceTypesArray.push('IA')
          } else {
            countyObject['ia_ih_assistance'] = false
          }

          if (obj.paProgramDeclared === true) {
            assistanceTypesArray.push('PA')
          }

          if (obj.hmProgramDeclared === true) {
            assistanceTypesArray.push('HM')
          }

          countyObject['assistance_types'] = assistanceTypesArray
        }
        
        // Determine for which service area(s) this county belongs to and add it (or if one service area for the state then just use that)
        if (potentialServiceAreas.length === 1) { 
          countyObject.serv_areas.push(potentialServiceAreas[0].serv_area)
          relevantAreas = potentialServiceAreas

        } else if (potentialServiceAreas.length > 1) {
          for (let i = 0; i < potentialServiceAreas.length; i++) {
            if (countiesPerArea[i].includes(`${countyArr[3]}${countyArr[4]}`)) {
              countyObject.serv_areas.push(potentialServiceAreas[i].serv_area)
              if (!(relevantAreas.some(x => x.serv_area === potentialServiceAreas[i].serv_area))) {
                relevantAreas.push(potentialServiceAreas[i])
              }
            }
          }
        }


        countiesArrayOfObjects.push(countyObject)

        // Save a list of any divided counties to display a warning note
        if (['04017', '04001', '25017', '25021', '25023', '06029', '06037', '06085', '72127'].indexOf(`${countyArr[3]}${countyArr[4]}`) > -1) { component.dividedCounties.push(countySansState) }

      })

      return [countiesArrayOfObjects, relevantAreas]
    },

    async getCountyAcs(stateFips, countyFipsList) {
      /**
       * Gets the ACS data from the Census API for the provided state and counties.
       * @param {string}  stateFips     String of two digit FIPS (i.e. '01')
       * @param {array}   countyFipsList Array of counties in the state as string FIPS (i.e. ['001', '002'])
       * @returns {array} Returns the 2D JSON array from the Census API
       */
      let data2DArray = await fetch(`https://api.census.gov/data/2020/acs/acs5/subject?get=NAME,S1701_C01_039E,S1701_C01_001E&for=county:${countyFipsList}&in=state:${stateFips}&key=${this.censusKey}`)
      .then(function (response) { 
        return response.json()
      })
      .catch((err) => {
        console.log(err)
      })
      return data2DArray
    },

    async getListOfCounties(stateFips) {
      /**
       * Gets the counties for a state using the Census API
       * @param {string}  stateFips     String of two digit FIPS (i.e. '01')
       * @returns N/A     Does not return anything. Sets the countyDropdown property to the result.
       */
      let counties = await fetch(`https://api.census.gov/data/2020/acs/acs5/subject?get=NAME&for=county:*&in=state:${stateFips}&key=${this.censusKey}`)
      .then(function (response) { 
        return response.json()
      })
      .catch((err) => {
        console.log(err)
      })

      // Convert from 2D JSON to object
      let countiesArrayOfObjects = []

      counties.forEach(function(countyArr) {
        if (countyArr[0] === 'NAME') { return }
        let countySansState = countyArr[0].substr(0, countyArr[0].lastIndexOf(","))
        let countyObject = {name: countySansState, fips: countyArr[2]}
        countiesArrayOfObjects.push(countyObject)
      })
      
      this.countyDropdownItems = countiesArrayOfObjects.sort((a, b) => a.name.localeCompare(b.name))

      return
    },

    formatThousandsNoDecimal (value) {
      return format(",.0f")(value)
    },

    sumOfArray (array) {
      return this.formatThousandsNoDecimal(d3_array.sum(array))
    },

    sumByArea (serv_area, counties, key) {
      /**
       * Filters an array of objects that contain the eligiblity estimates for declared counties to only those for which the serv_areas values includes the provided serv_area.
       * 
       * For example, if serv_area is 'VA-15' then will return any object in the array (any county) associated in which serv_areas contains 'VA-15', such as ['VA-15'] or ['VA-11', 'VA-15'], etc.
       * 
       * @param {string}  serv_area The service area ID such as 'PR-1'
       * @param {array}   counties  An array of objects of designated counties, such as [{ "county": "Anson County", "eligible": 5544, "state_fips": "37", "county_fips": "007", "serv_areas": [ "NC-5" ] }, { "county": "Beaufort County", "eligible": 10883, "state_fips": "37", "county_fips": "013", "serv_areas": [ "NC-5" ] }]
       * @param {string}  key       The key in the counties array to sum, such as "eligible" or "pop"
       * 
       * @return {number} Returns the sum of the "eligible" population key from all objects in the array after filtering to those related to the service area.
       */
      let counties_f = counties.filter(x => x.serv_areas.includes(serv_area))
      let array_f = counties_f.map(x => x[key])
      return this.formatThousandsNoDecimal(d3_array.sum(array_f))
    },


    async prepareStateSummary () {
      /**
       * Main function for the counties by state tab, which works through the steps of retrieving and displaying the population and eligibility estimates for counties in a selected state.
       * @return N/A   Does not return anything, but it sets as a reactive property the resulting data.
       */
      this.stateLoading = true
      this.stateLoadedOnce = true

      const counties = this.selectAllCounties === true ? '*' : JSON.parse(JSON.stringify(this.countyDropdownSelected))

      // Get associated service areas for the state
      let potentialServiceAreas = this.getPotentialServiceAreas(this.selectedStateAbbrev)

      // For multi-area states, get each service area's associated counties
      const countiesPerArea = await this.getCountiesPerArea(potentialServiceAreas)

      // Get population estimates from the Census API for the counties
      // let counties = disasterCountyDeclarations.map(x => x.fipsCountyCode)
      let r = await this.prepareCountyEstimates(this.selectedState, counties, potentialServiceAreas, countiesPerArea)
      this.stateRelevantAreas = r[1]
      let sortedCounties = r[0].sort((a, b) => a.county.localeCompare(b.county))
      this.stateRequestedCounties = sortedCounties
      this.stateLoading = false

      this.goTo('stateResults')
    },

    applySelectAllCounties (e) {
      if (e === true) {
        this.countyDropdownSelected = this.countyDropdownItems
      }
    },

    goTo (refName) {
      const element = this.$refs[refName]
      const top = element.offsetTop
      window.scrollTo(0, top)
    },

    handleStateChange (st) {
      this.selectedState = st
    }
  },

  watch: {
    async selectedState (to) {
      if (to !== null && to !== undefined && this.tab === 1) {
        this.countyDropdownSelected = []
        this.selectAllCounties = false
        this.getListOfCounties(to)
      }
    },
    tab (to) {
      if (to === 0 && this.disastersList.length === 0) {
        this.getMoreDisasters()
      }
      if (to === 1 && this.selectedState !== '' && this.selectedState !== '*') {
        this.getListOfCounties(this.selectedState)
      }
    },
    selectAllCounties(to) {
      this.applySelectAllCounties(to)
    },
    countyDropdownSelected() {
      if (this.countyDropdownSelected.length < this.countyDropdownItems.length) {
        this.selectAllCounties = false
      }
    }
  }
}
</script>

<style lang="scss">
@import '@/scss/vars.scss';
#disaster-estimates {
  .disaster-header {
    padding-bottom: 1rem !important;
    padding-top: 1rem !important;
    &:hover {
      background-color: $extra-subtle-blue;
      transition: 0.3s;

      .incident-icon {
        color: #5a5a5a !important;
      }
    }
    &.v-expansion-panel-header--active {
      background-color: $subtle-blue;

      .incident-icon {
        color: $header-red !important;
      }
    }
  }
  .header-flex {
    display: flex;
    justify-content: space-between;
    align-items: center;
    .header-left {
      display: flex;
    }
    .declaration-type {
      margin-right: 2rem;
    }
    .header-name {
      padding-left: 1rem;
      font-family: Work Sans, sans-serif;
      font-weight: 400;
    }
    .disaster-number {
      font-size: 0.8rem;
      margin-top: 0.5rem;
      color: rgb(119, 119, 119);
    }
    .header-flex-column {
      display: flex;
      flex-direction: column;
    }
    .incident-icon {
      width: 2rem;
      margin-right: 1.75rem;
    }
  }
  #estimate-tabs {
    margin-top: 3rem;
    margin-bottom: 2rem;
  }
  #disasters-expansion-panels {
    margin-top: 2rem;
  }
  .disaster-content {
    .v-expansion-panel-content__wrap {
      padding: 0;
    }
    .disaster-content-margins {
      margin: 1rem 4.75rem 3.5rem 4.75rem;
    }
    .link-to-map {
      display: flex;
      justify-content: space-between;
      padding: 0.75rem 2rem 0.75rem 5rem;
      background-color: $extra-subtle-blue;

      .assistance-type-radio {
        margin: 0;
        
        >div {
          margin: 0;
          padding: 0;
          
          .v-label {
            font-size: 0.9rem;
            color: rgba(0, 0, 0, 0.87);
          }
        }
      }
    }
    .link-to-map-icon {
      margin-right: 0.25rem;
      margin-top: -0.325rem;
    }
    table {
      margin: 0.5rem 2rem 1rem 1rem;
      font-size: 0.9rem;

      thead {
        color: rgb(123, 123, 123);
        font-style: italic;

        td {
          border-bottom: 1px solid #ddd;
        }
      }
      td {
        padding: 0.25rem 2rem;
      }
      tr.hidden-row {
        display: none;
      }
    }
    .table-note {
      font-size: 0.9rem;
      font-style: italic;
      margin-left: 3rem;
    }
    .summary-subheader {
      margin-top: 2.75rem;
      color: $header-red;
      text-transform: uppercase;
      font-size: 0.85rem;
    }
    .eligible-total {
      margin-top: 2.5rem;

      .eligible-total-num {
        color: $main-blue;
        padding-right: 0.5rem;
        font-size: 1.5rem;
      }
    }
    .counties-table {
      max-height: 20rem;
      overflow-y: auto;

      &.table-overflow {
        background-image: linear-gradient(to bottom, #fff 85%, #f5f5f5 99%);
      }
    }
    .no-declared-county {
      font-style: italic;
      margin-top: 2rem;
    }
  }
  .state-content-margins {
    margin: 2rem 0 2rem 0;
    border-radius: 0.25rem;
    box-shadow: 0px 3px 1px -2px rgb(0 0 0 / 20%), 0px 2px 2px 0px rgb(0 0 0 / 14%), 0px 1px 5px 0px rgb(0 0 0 / 12%);

    .state-content-header {
      padding: 1rem;
      padding-left: 1rem;
      font-family: Work Sans, sans-serif;
      text-transform: uppercase;
      font-weight: 400;
      background-color: $subtle-blue;
    }

    .grantee-county-summary {
      padding: 1rem 3rem 3rem 3rem;
    }

    .counties-table {
      max-height: 30rem;
    }
  }
  .onthemap-info-button {
    border: none !important;
    margin-top: 1rem;

    span.v-btn__content {
      font-weight: normal;
      color: #4a4a4a;
    }
  }
  .load-more {
    margin: 2rem auto;
    display: block;
  }
  .disaster-content-loading {
    padding-top: 1rem;
    position: relative;
    z-index: 50;
    min-height: 6.5rem;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    font-size: 0.8rem;

    > div {
      padding: 0.25rem 0;
    }

    .spinner {
      display: inline-block;
      width: 50px;
      height: 50px;
      border: 3px solid rgba(216, 216, 216, 0.888);
      border-radius: 50%;
      border-top-color: $main-blue;
      animation: spin 1s ease-in-out infinite;
      -webkit-animation: spin 1s ease-in-out infinite;
    }
    @keyframes spin {
      to { -webkit-transform: rotate(360deg); }
    }
    @-webkit-keyframes spin {
      to { -webkit-transform: rotate(360deg); }
    }
  }
  .state-county-select {
    margin: 0 auto 2rem 1rem;
  }
  .select-county-header-row {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;

    .v-input__slot {
      margin-bottom: 0;
    }
  }
  .counties-count {
    margin-left: 1rem;
  }
  .select-county-instruction {
    font-size: 0.85rem;
    font-style: italic;
    margin-left: 1rem;
  }
  .state-select {
    max-width: 25rem;
  }
  .county-select {
    max-width: 60rem;
    max-height: 10rem;
    overflow-y: auto;
    overflow-x: hidden;

    &.table-overflow {
      background-image: linear-gradient(to bottom, #fff 85%, #f5f5f5 99%);
    }
  }
  .v-list-item {
    max-height: 2rem !important;

    .v-list-item__content {
      padding: 0.5rem 0 !important;
    }
  }
  .ready-btn {
    margin: 0.5rem auto;
    display: block;
  }
  .tab-instructions {
    margin-bottom: 2rem;
    font-size: 0.95rem;
    font-style: italic;

    .emphasis {
      text-decoration: underline;
    }
  }
  .select-all-counties-container {
    display: flex;
  }
  #disaster-list-filters {
    padding: 1rem;
    border: 1px dashed rgba($header-red, 0.1625);
    border-radius: 1rem;
    margin: 1rem 1rem 4rem 1rem;

    h3 {
      text-transform: uppercase;
      letter-spacing: 0.125rem;
      padding-left: 1rem;
    }

    #filters-row {
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-start;

      >div {
        display: flex;
        flex-direction: column;
        padding: 1rem;
        margin-right: 2rem;

        .filter-header {
          font-style: italic;
          font-size: 0.95rem;
        }
        .filter-select {
          max-width: 15rem;
          padding-top: 1rem;
          margin-top: 0.25rem;
        }
      }
    }
  }
  #no-more-to-load {
    font-style: italic;
    margin-top: 1rem;
    text-align: center;
    color: $header-red;
    font-weight: bold;
  }
}
#onthemap-intro {
  margin-bottom: 1rem;
}
#onthemap-img {
  margin: 0 auto;
  display: block;
  max-width: 40rem;
  width: 100%;
  height: auto;
}
#onthemap-dialog-contents {
  padding: 2rem 0;
}
</style>